import React, { useEffect } from 'react'

import {
  Label,
  Loader,
  BackToTop,
  OrderListItem,
  Breadcrumbs,
  HeadingLarge,
} from '@pb/components'
import { Tabs } from '@hm-group/fabric-components'
import { Breakpoints, getLabelFromStorage, useMediaQuery } from '@pb/utils'
import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'

import { ACCOUNT_ROUTES, EXTERNAL_ROUTES } from 'App/constants'
import appSelectors, { getShouldShowPayLaterPostPurchaseMenuItem } from 'App/duck/selectors'
import optimizelyConstants from 'constants/optimizely'
import { ACCOUNT_TRACKING_EVENTS } from 'features/account/constants'
import { PAGE_IDS } from 'features/account/helpers/getAccountTrackingData'
import useAccountFeature from 'features/account/hooks/useAccountFeature'
import useOptimizelyDecision, {
  useNorthstarABTestEnabled,
} from 'features/account/hooks/useOptimizelyDecision'
import useOptimizelyToggle from 'features/account/hooks/useOptimizelyToggle'
import useTrackAccountClickEvent from 'features/account/hooks/useTrackAccountClickEvent'
import useTrackAccountPageView from 'features/account/hooks/useTrackAccountPageView'
import { operationSetJoinClubModalVisibility } from 'features/account/hub/duck/operations'
import MyAccountLayout from 'features/account/MyAccountLayout'
import selectors from 'features/account/purchases/duck/selectors'
import { trackOptimizelyEvent } from 'helpers/optimizely'

import styles from './AllPurchasesOverview.scss'
import { ONLINE_TAB, STORE_TAB } from '../../constants'
import {
  operationFetchOnlineOrders,
  operationFetchStoreOrders,
  operationGetOnlineOrderDetailsById,
  operationGetStoreOrderDetailsById,
} from '../../duck/operations'
import OnlineOrdersList from '../OnlineOrdersList'
import OrderError from '../OrderError'
import StoreOrdersList from '../StoreOrdersList'

const AllPurchasesOverview: React.FC = () => {
  const dispatch = useDispatch()

  const {
    pages: {
      allPurchase: { shouldShowStoreReceipts, shouldShowMyInvoicesKlarna },
    },
  } = useAccountFeature()

  const isOnlineOrdersLoading = useSelector(selectors.getIsOnlineOrdersLoading)
  const isStoreOrdersLoading = useSelector(selectors.getIsStoreOrdersLoading)
  const isStaffMember = useSelector(appSelectors.getIsStaffMember)
  const userIsFullMember = useSelector(appSelectors.getUserIsFullMember)
  const selectedTab = useSelector(selectors.getSelectedTab)
  const onlineOrders = useSelector(selectors.getOnlineOrders)
  const shouldShowLoadMoreOnlineButton = useSelector(selectors.getShouldShowLoadMoreOnlineButton)
  const shouldShowLoadMoreStoreButton = useSelector(selectors.getShouldShowLoadMoreStoreButton)
  const shouldShowOnlineLoader = useSelector(selectors.shouldShowOnlineLoader)
  const shouldShowStoreLoader = useSelector(selectors.shouldShowStoreLoader)
  const isOnlineReceiptEnabled = useSelector(selectors.getIsOnlineReceiptEnabled)
  const isDigitalReceiptEnabled = useSelector(appSelectors.getIsDigitalReceiptEnabled)
  const storeOrders = useSelector(selectors.getStoreOrders)
  const isOnlineOrdersLoadingFailed = useSelector(selectors.getIsOnlineOrdersLoadingFailed)
  const isStoreOrdersLoadingFailed = useSelector(selectors.getIsStoreOrdersLoadingFailed)
  const isStoreOrderAddressesFailed = useSelector(selectors.getIsStoreOrderAddressesFailed)
  const shouldFetchOnlineOrders = useSelector(selectors.getShouldFetchOnlineOrders)
  const shouldFetchStoreOrders = useSelector(selectors.getShouldFetchStoreOrders)
  const shouldShowTrackingId = useSelector(selectors.getShouldShowTrackingId)
  const trackPageView = useTrackAccountPageView()
  const trackEvent = useTrackAccountClickEvent()
  const totalOrdersCount = useSelector(selectors.getTotalOrdersCount)
  const isPostPurchaseEnabled = useSelector(getShouldShowPayLaterPostPurchaseMenuItem)
  const isNorthstarBrand = useOptimizelyDecision(optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION)

  const isNorthStarABtestEnabled = useNorthstarABTestEnabled(
    optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION,
  )

  const loadMoreOnlineOrders = (): any => {
    dispatch(operationFetchOnlineOrders())
  }
  const fetchOnlineOrders = (): any => dispatch(operationFetchOnlineOrders())
  const fetchStoreOrders = (): any => dispatch(operationFetchStoreOrders())
  const setJoinClubModalVisibility = (isVisible): any =>
    dispatch(operationSetJoinClubModalVisibility(isVisible))
  const getOnlineOrderDetailsById = (orderId): any =>
    dispatch(operationGetOnlineOrderDetailsById(orderId))
  const getStoreOrderDetailsById = (orderId): any =>
    dispatch(operationGetStoreOrderDetailsById(orderId))
  const trackOnlineTabClick = trackEvent(ACCOUNT_TRACKING_EVENTS.clickOnlineOrdersTab)
  const trackStoreTabClick = trackEvent(ACCOUNT_TRACKING_EVENTS.clickStoreOrdersTab)

  const trackKlarnaOrderItemClick = (): void => {
    trackEvent(ACCOUNT_TRACKING_EVENTS.onKlarnaOrderItemClick)()
  }

  const trackOrderItemClick = (): void => {
    trackEvent(ACCOUNT_TRACKING_EVENTS.onOrderItemClick)()
    if (isNorthStarABtestEnabled) {
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_ORDER_CLICK)
    }
  }

  const isStaticUserMenuEnabled = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_STATIC_MENU_ENABLE,
    internalToggle: 'static_menu_internal_toggle',
  })
  const XLargeBreakpoint = useMediaQuery(Breakpoints.XLARGE)

  const { AccountOverview, Purchases } = ACCOUNT_ROUTES

  const currentRoute = useLocation().pathname

  const breadcrumbLinks = [
    { href: EXTERNAL_ROUTES.index, label: getLabelFromStorage('breadcrumb.home') },
    {
      label: getLabelFromStorage('hub.my.account.title'),
      Tag: Link,
      customProps: {
        to: `${AccountOverview.path}`,
      },
    },
    ...(currentRoute !== Purchases.path // Don't add this breadcrumb if on root purchases page
      ? [
          {
            label: getLabelFromStorage('account.my.setting'),
            Tag: Link,
            customProps: {
              to: `${Purchases.path}`,
            },
          },
        ]
      : []),
    {
      label: getLabelFromStorage('profile.hmOrders.view.orders'),
    },
  ]

  useEffect(() => {
    if (shouldFetchOnlineOrders) {
      fetchOnlineOrders()
    }
    if (shouldFetchStoreOrders) {
      fetchStoreOrders()
    }
    trackPageView(PAGE_IDS.purchases)
    if (isNorthStarABtestEnabled) {
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_ORDER_LIST_PAGEVIEW)
    }
  }, [])

  useEffect(() => {
    if (selectedTab === ONLINE_TAB.id) {
      trackOnlineTabClick()
    } else {
      trackStoreTabClick()
    }
  }, [selectedTab])

  const renderOnlineTabContent = (): any => {
    switch (true) {
      case shouldShowOnlineLoader:
        return <Loader />
      case isOnlineOrdersLoadingFailed:
        return (
          <OrderError
            headline={<Label copyKey="account.digitalreceipts.sorry" />}
            text={<Label copyKey="account.digitalreceipts.cannot.display.receipts" />}
          />
        )
      case onlineOrders.length === 0:
        return (
          <OrderError
            headline={<Label copyKey="account.digitalreceipts.sorry" />}
            text={<Label copyKey="account.digitalreceipts.info" />}
            button={{
              text: <Label copyKey="account.digitalreceipts.takemetofashion" />,
              link: EXTERNAL_ROUTES.homePage,
              type: 'anchor',
            }}
          />
        )
      default:
        return (
          <>
            {isPostPurchaseEnabled && shouldShowMyInvoicesKlarna && (
              <>
                <OrderListItem
                  orderStatusDisplay={
                    isNorthstarBrand
                      ? getLabelFromStorage('account.klarna.link.ns')
                      : getLabelFromStorage('account.klarna.link')
                  }
                  orderDetailRoute={ACCOUNT_ROUTES.HMPay.path}
                  className={
                    !isNorthstarBrand ? styles.defaultKlarnaOrderItem : styles.klarnaOrderItem
                  }
                  onClick={trackKlarnaOrderItemClick}
                  hasDefault={!isNorthstarBrand}
                />
                {isNorthstarBrand && <hr className={styles.line} />}
              </>
            )}
            <OnlineOrdersList
              loadMoreOrders={loadMoreOnlineOrders}
              isOrdersLoading={isOnlineOrdersLoading}
              orders={onlineOrders}
              shouldShowLoadMoreButton={shouldShowLoadMoreOnlineButton}
              shouldShowTrackingId={shouldShowTrackingId}
              getOnlineOrderDetailsById={getOnlineOrderDetailsById}
              isOnlineReceiptEnabled={isOnlineReceiptEnabled}
              isStaffMember={isStaffMember}
              isDigitalReceiptEnabled={isDigitalReceiptEnabled}
              onOrderItemClick={trackOrderItemClick}
            />
          </>
        )
    }
  }

  const renderStoreTabContent = (): any => {
    switch (true) {
      case shouldShowStoreLoader:
        return <Loader />
      case isStoreOrdersLoadingFailed:
        return (
          <OrderError
            headline={<Label copyKey="account.digitalreceipts.sorry" />}
            text={<Label copyKey="account.digitalreceipts.cannot.display.receipts" />}
          />
        )
      case !userIsFullMember:
        return (
          <OrderError
            headline={<Label copyKey="account.digitalreceipts.sorry" />}
            text={<Label copyKey="account.digitalreceipts.upgrade" />}
            button={{
              text: <Label copyKey="account.digitalreceipts.becomeamember" />,
              onClick: () => setJoinClubModalVisibility(true),
              link: ACCOUNT_ROUTES.AccountOverview.path,
              type: 'link',
            }}
          />
        )
      case storeOrders.length === 0:
        return (
          <OrderError
            headline={<Label copyKey="account.digitalreceipts.sorry" />}
            text={<Label copyKey="account.digitalreceipts.info2" />}
            button={{
              text: <Label copyKey="account.digitalreceipts.goback" />,
              link: ACCOUNT_ROUTES.AccountOverview.path,
              type: 'link',
            }}
          />
        )
      default:
        return (
          <StoreOrdersList
            isOrdersLoading={isStoreOrdersLoading}
            orders={storeOrders}
            shouldShowLoadMoreButton={shouldShowLoadMoreStoreButton}
            getStoreOrderDetailsById={getStoreOrderDetailsById}
            isStoreOrderAddressesFailed={isStoreOrderAddressesFailed}
          />
        )
    }
  }

  const renderClubMarketContent = (): any => (
    <Tabs
      tabs={[
        {
          content: renderOnlineTabContent(),
          id: 0,
          label: !isNorthstarBrand
            ? `${getLabelFromStorage(ONLINE_TAB.text)} (${totalOrdersCount.online || 0})`
            : `${getLabelFromStorage(ONLINE_TAB.text)} [${totalOrdersCount.online || 0}]`,
        },
        {
          content: renderStoreTabContent(),
          id: 1,
          label: !isNorthstarBrand
            ? `${getLabelFromStorage(STORE_TAB.text)} (${totalOrdersCount.store || 0})`
            : `${getLabelFromStorage(STORE_TAB.text)} [${totalOrdersCount.store || 0}]`,
        },
      ]}
      variant="fixed"
    />
  )
  const shouldNotShowStaticMenu = XLargeBreakpoint || !isStaticUserMenuEnabled
  return (
    <main
      id="main-content"
      className={cn(
        styles.container,
        !isNorthstarBrand
          ? shouldNotShowStaticMenu
            ? styles.containerLarge
            : styles.containerXLarge
          : styles.containerPadding,
      )}
    >
      <Breadcrumbs
        links={breadcrumbLinks}
        shouldHideBreadcrumbs={useMediaQuery(Breakpoints.LARGE)}
      />
      <MyAccountLayout isDisabled={shouldNotShowStaticMenu}>
        <HeadingLarge Tag="h1" className={styles.heading}>
          <Label copyKey="profile.hmOrders.view.orders" />
        </HeadingLarge>
        {isDigitalReceiptEnabled && shouldShowStoreReceipts
          ? renderClubMarketContent()
          : renderOnlineTabContent()}
        {/* TODO: Add copyKey with translation for srLabel, will be done with CRMMA-3127 */}
        <BackToTop label="Back to top" />
      </MyAccountLayout>
    </main>
  )
}

export default AllPurchasesOverview
